import styled from '@emotion/styled';
import type { Theme } from '@trinity/chat-components/types/theme';
import { getThemeValue, getThemeColor, THEME } from './DynamicForm.styles.theme';

type StyledComponentProps = {
  theme?: Theme.Theme;
};

// =============================================================================
// BASE COMPONENTS
// =============================================================================

export const FormContainer = styled.div<StyledComponentProps>(({ theme }) => ({
  backgroundColor: getThemeColor.background(theme),
  padding: 0,
}));

export const BaseHeader = styled.div<StyledComponentProps>(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
}));

export const BaseIcon = styled.div<StyledComponentProps>(({ theme }) => ({
  width: '40px',
  height: '40px',
  backgroundColor: getThemeColor.primary(theme),
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: getThemeColor.background(theme),
  flexShrink: 0,
}));

export const FormTitle = styled.h1<StyledComponentProps>(({ theme }) => ({
  fontSize: THEME.fontSize.base,
  color: getThemeColor.text(theme),
  fontFamily: getThemeValue(
    (t) => t.fonts?.regular,
    'inherit',
    theme,
  ),
  lineHeight: 1.6,
  margin: `0 0 ${THEME.spacing.md} 0`,
}));

export const BaseRow = styled.div<StyledComponentProps>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: THEME.spacing.xs,
  marginBottom: THEME.spacing.lg,
}));

export const Message = styled.p<StyledComponentProps>(({ theme }) => ({
  color: getThemeColor.text(theme),
  fontSize: THEME.fontSize.base,
  lineHeight: 1.6,
  margin: `0 0 ${THEME.spacing.md} 0`,
}));

export const StyledList = styled.ul<StyledComponentProps>(({ theme }) => ({
  color: getThemeColor.text(theme),
  fontSize: THEME.fontSize.base,
  paddingLeft: THEME.spacing.xl,
  margin: `0 0 ${THEME.spacing.md} 0`,
  '& li': {
    marginBottom: THEME.spacing.sm,
  },
}));

export const WarningText = styled.div<StyledComponentProps>(({ theme }) => ({
  color: getThemeColor.text(theme),
  fontSize: THEME.fontSize.base,
  margin: `${THEME.spacing.xl} 0`,
  fontStyle: 'italic',
}));

export const VoiceWarningBox = styled.div<StyledComponentProps>(({ theme }) => {
  return {
    backgroundColor: getThemeColor.backgroundLight(theme),
    borderRadius: THEME.borderRadius.md,
    padding: THEME.spacing.lg,
    marginTop: THEME.spacing.lg,
    color: getThemeColor.text(theme),
    fontSize: THEME.fontSize.sm,
    lineHeight: 1.6,
    fontFamily: getThemeValue(
      (t) => t.fonts?.regular,
      'inherit',
      theme,
    ),
    fontStyle: 'italic',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
  };
});

export const Divider = styled.hr<StyledComponentProps>(({ theme }) => ({
  border: 'none',
  borderTop: `1px solid ${getThemeColor.border(theme)}`,
  margin: `${THEME.spacing.xl} 0`,
}));

export const ButtonContainer = styled.div<StyledComponentProps>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: THEME.spacing.md,
  marginTop: THEME.spacing.lg,
}));

export const ActionButtonContainer = styled.div<StyledComponentProps>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: THEME.spacing.md,
  marginTop: THEME.spacing.xl,
}));

// Component aliases
export const FormHeader = BaseHeader;
export const FormIcon = BaseIcon;
export const FormRow = BaseRow;

